import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import checkIfAuthenticatedAndAuthenticate from "Logic/authentication";
import Admin from "Pages/admin";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { loadStyles, setHalfPage } from "feature/app/actions";
import { providerConfig } from "index";
import { ReactNode, useCallback, useEffect, useState } from "react";
import {
  Outlet,
  Route,
  RouterProvider,
  Routes,
  createBrowserRouter,
  useSearchParams,
} from "react-router-dom";
import { ElementType } from "types/types";
import LinkEditor from "./Components/Molecule/LinkEditor";
import Menu from "./Components/Molecule/Menu";
import ReferenceEditor from "./Components/Molecule/ReferenceEditor";
import TermEditor from "./Components/Molecule/TermEditor";
import Module from "./Pages/Module";
import Modules from "./Pages/Modules";
import ProducerElement from "./Pages/ProducerElement";
import ProducerModule from "./Pages/ProducerModule";
import {
  initializeUserByToken,
  loadUserData,
  pushNobocapDetailsToBackend,
  resetLoadingAnimation,
  selectHasUserData,
  selectIsMenuShowing,
  selectOrganizations,
  selectUserLinks,
  selectUserRefs,
  selectUserTerms,
  setClientDetails,
  setLoading,
  setLoadingAnimation,
} from "./Redux/UserSlice";
import {
  selectAuthType,
  selectError,
  selectIsAuth0AuthenticationUsed,
  selectIsMSAuthenticationUsed,
  selectIsNobocapAuthenticationUsed,
  selectNobocapDetails,
  selectStyle,
  setAuthType,
  setLoadingFalse,
  validateNobocapToken,
} from "./Redux/appSlice";
import Organization from "Pages/admin/Organization";
import AllUsers from "Pages/admin/AllUsers";
import UnassignedUsers from "Pages/admin/UnassignedUsers";
import PendingUsers from "Pages/admin/PendingUsers";
import ModuleErrorBoundary from "Components/Atoms/ModuleErrorBoundary";
import { useSelector } from "react-redux";
import Logo from "Components/Atoms/Logo";
import { Honeybadger, HoneybadgerErrorBoundary } from "@honeybadger-io/react"

const INVITATION_CODE_QUERY_PARAMETER = "invi_code";
const config = {
  apiKey: "hbp_JhhGDI4tZSbEkgMy1blZdLZ1kll6IR3d9Kn0",
  environment: "testing",
  // Reporting is switched off while the Honeybadger quota is exhausted. The
  // error boundary still catches and renders, it just does not transmit.
  // Re-enable by removing this line.
  reportData: false,
  // revision: "git SHA/project version"
}
const honeybadger = Honeybadger.configure(config);

function Root() {
  const dispatch = useAppDispatch();
  const [invitationCode, setInvitationCode] = useState<string | null>();
  const [params, setParams] = useSearchParams();
  const [nobocapToken, setNobocapToken] = useState<string | null>(params.get("token"));
  const [token, setToken] = useState<string>();
  const userTerms = useAppSelector(selectUserTerms);
  const userLinks = useAppSelector(selectUserLinks);
  const userRefs = useAppSelector(selectUserRefs);
  const isMsAuthenticationUsed = useAppSelector(selectIsMSAuthenticationUsed);
  const isAuth0AuthenticationUsed = useAppSelector(
    selectIsAuth0AuthenticationUsed
  );
  const hasUserData = useAppSelector(selectHasUserData);

  const [referrer, _] = useState<string>(document.referrer);
  const organizations = useAppSelector(selectOrganizations);
  const [initDone, setInitDone] = useState<boolean>(false);

  const authType = useSelector(selectAuthType);
  const { logo, color1, color2 } = useAppSelector(selectStyle);
  useEffect(() => {
    if (isAuth0AuthenticationUsed || isMsAuthenticationUsed) {
      dispatch(
        setClientDetails({
          clientId: window.localStorage.getItem("clientId"),
          tenantId: window.localStorage.getItem("tenantId"),
        })
      );
    }
  }, [isAuth0AuthenticationUsed, isMsAuthenticationUsed]);


  useEffect(() => {
    function handleResize() {
      dispatch(setHalfPage(window.innerWidth > 1024));
    }

    dispatch(setHalfPage(window.innerWidth > 1024));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!token) return;

    if (!hasUserData) {
      dispatch(setLoadingAnimation());
      dispatch(setLoading());
      dispatch(initializeUserByToken({ invitationCode })).then(() => setTimeout(() => setInitDone(true), 500));
    }
  }, [dispatch, hasUserData, invitationCode, token]);

  const onGetToken = useCallback((token: string) => setToken(token), []);

  useEffect(() => {
    dispatch(setLoading());
    dispatch(setLoadingAnimation());
    if (organizations && initDone) {
      dispatch(loadStyles(organizations.length > 1 ? undefined : organizations[0]));
      dispatch(
        loadUserData({
          organisationIds: organizations.map((o) => o.organisation_id),
        })
      ).then(() => dispatch(resetLoadingAnimation()));
    }
  }, [dispatch, organizations.length, initDone]);

  const body = (
    <div>
      <HoneybadgerErrorBoundary honeybadger={honeybadger}>
        <Routes>
          <Route element={<Layout />}>
            <Route
              path="/health"
              element={
                <>
                  <h3>ok</h3>
                </>
              }
            />
            <Route
              path="producer/organization/:orgId/module/:moduleId/terms"
              element={
                <>
                  <ProducerElement
                    elements={userTerms}
                    Editor={TermEditor}
                    type={ElementType.Term}
                  />
                </>
              }
            />
            <Route
              path="producer/organization/:orgId/module/:moduleId/links"
              element={
                <>
                  <ProducerElement
                    elements={userLinks}
                    Editor={LinkEditor}
                    type={ElementType.Link}
                  />
                </>
              }
            />
            <Route
              path="producer/organization/:orgId/module/:moduleId/references"
              element={
                <>
                  <ProducerElement
                    elements={userRefs}
                    Editor={ReferenceEditor}
                    type={ElementType.Reference}
                  />
                </>
              }
            />
            <Route
              path="/"
              element={
                <>
                  <Modules />
                </>
              }
            />
            <Route
              path="organization/:orgId/module/:moduleId"
              element={
                <>
                  <ModuleErrorBoundary>
                    <Module />
                  </ModuleErrorBoundary>
                </>
              }
            />
            <Route
              path="producer/organization/:orgId/module/:moduleId"
              element={
                <>
                  <ProducerModule />
                </>
              }
            />
          </Route>
        </Routes>
      </HoneybadgerErrorBoundary>
    </div>
  );

  const NOBOCAP = "nobocap";
  const AUTH0 = "auth0";
  function isNobocap() {
    return (localStorage.getItem("portal") == NOBOCAP || referrer.indexOf(NOBOCAP) > -1) && (localStorage.getItem("token") != "" || (nobocapToken != "" && nobocapToken != null && nobocapToken != undefined));
  }
  // use of local storage to handle refresh issue PLUS E-Tool link access logic 
  if (isNobocap()) {
    const lsToken = localStorage.getItem("token");
    return <ExternalProvider token={nobocapToken || lsToken} type={NOBOCAP}>
      {body}
    </ExternalProvider>
  }

  if(authType == "auth0" || (params.get(INVITATION_CODE_QUERY_PARAMETER)) || localStorage.getItem("portal") == AUTH0) {
    localStorage.setItem("portal", AUTH0);
    return (
      <Auth0Provider
        {...providerConfig}
        onRedirectCallback={(appState) => {
          window.history.replaceState({}, document.title, "/");
          if (appState?.additionalDetails?.authType == "nobocap") {
            setToken(appState?.invitationCode)
          }
          setInvitationCode(appState?.invitationCode);
        }}
      >
        <Auth0Wrapper onGetToken={onGetToken}>{body}</Auth0Wrapper>
      </Auth0Provider>
    );
  }

  if(authType == "nobocap") {
    redirectToNobocap();
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="card bg-white shadow-md rounded-lg overflow-hidden">
        <div className="card-header bg-blue-500 text-white font-bold py-4 px-6 flex justify-between items-center" style={{ background: color1 }}>
          <div className="logo-container">
          <Logo image={logo} />
            <link rel="icon" href="localhost:3000/public/favicon.ico" />
          </div>
          <h2 className="text-2xl">Select Auth Mechanism</h2>
        </div>
        <div className="card-body p-6">
          <div className="flex justify-center space-x-4">
            <button className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded" onClick={() => dispatch(setAuthType("auth0"))} style={{ background: color1 }}>Auth-0</button>
            <button className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded" onClick={() => dispatch(setAuthType("nobocap"))} style={{ background: color1 }}>Nobocap</button>
          </div>
        </div>
      </div>
    </div>
  );


  
}

const ExternalProvider = ({ token, children, type }: any) => {
  console.log("EXternal provider render", token, type);
  const dispatch = useAppDispatch();
  const [errorState, setErrorState] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [params, setParams] = useSearchParams();
  const nobocapDetails = useSelector(selectNobocapDetails);
  const error = useSelector(selectError);
  const organizations = useAppSelector(selectOrganizations);
  useEffect(() => {
    if (!token)
      return;
    if (type == "nobocap") {
      localStorage.setItem("portal", type);
      dispatch(validateNobocapToken({ token }));
      localStorage.setItem("token", token);
      dispatch(setAuthType("nobocap"));
      window.userToken = token;
      if (params.get("token") == null) {
        setParams({"token": token});
      }
    }
  }, [token]);

  useEffect(() => {
    if (nobocapDetails.email) {
      dispatch(setAuthType("nobocap"));
      if (type == "nobocap")
        dispatch(pushNobocapDetailsToBackend({ token, data: nobocapDetails })).then(() => setIsAuthenticated(true));
    }
  }, [nobocapDetails]);

  useEffect(() => {
    if (error || errorState) {
      logout();
    }
  }, [error])

  useEffect(() => {
    if (isAuthenticated) {
      if (organizations) {
        dispatch(loadStyles(organizations.length > 1 ? undefined : organizations[0]));
      } else {
        dispatch(loadStyles());
      }
      if (organizations) {
        dispatch(loadStyles(organizations.length > 1 ? undefined : organizations[0]));
        dispatch(
          loadUserData({
            organisationIds: organizations.map((o) => o.organisation_id),
          })
        );
      }
      dispatch(setAuthType("nobocap"));
    }
  }, [isAuthenticated])

  // TODO: link it with logout
  const logout = () => {
    localStorage.setItem("token", "");
    window.userToken = "";
    dispatch(setAuthType("nobocap"));
    localStorage.setItem("portal", "");
    setIsAuthenticated(false);
    redirectToNobocap();
  }

  return <>
    {
      errorState || !isAuthenticated ? <Layout></Layout> : <div>{children}</div>
    }
  </>;
}


function Layout() {
  const isMenuShowing = useAppSelector(selectIsMenuShowing);
  return (
    <div className="relative overflow-hidden">
      {isMenuShowing && <Menu />}
      <Outlet />
    </div>
  );
}

type Auth0WrapperProps = {
  children: ReactNode;
  onGetToken: (token: string) => void;
};

function Auth0Wrapper({ children, onGetToken }: Auth0WrapperProps) {
  const [searchParams] = useSearchParams();
  const {
    loginWithRedirect,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const organizations = useAppSelector(selectOrganizations);

  useEffect(() => {
    (async () => {
      const token = await getAccessTokenSilently();
      window.userToken = token;
      window.localStorage.setItem("token", token);
      onGetToken(token);
    })();

    return () => { };
  }, []);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (organizations) {
      dispatch(loadStyles(organizations.length > 1 ? undefined : organizations[0]));
    } else {
      dispatch(loadStyles());
    }
    if(isAuthenticated) {
      dispatch(setAuthType("auth0"));
    }
    checkIfAuthenticatedAndAuthenticate(
      () => {
        return loginWithRedirect({
          appState: {
            invitationCode: searchParams.get(INVITATION_CODE_QUERY_PARAMETER),
            additionalDetails: {}
          },
        })
      },
      isAuthenticated,
      isLoading
    );
  }, [dispatch, isAuthenticated, isLoading, loginWithRedirect, organizations]);

  return <>{children}</>;
}

const Auth0ProviderLayout = () => {
  return localStorage.getItem("portal") == "nobocap" ?
    <ExternalProvider token={localStorage.getItem("token")} type="nobocap"><Outlet /></ExternalProvider> :
    <Auth0Provider {...providerConfig}>
      <Outlet />
    </Auth0Provider>
};

const router = createBrowserRouter([
  { path: "*", element: <Root /> },
  {
    path: "admin/",
    element: <Auth0ProviderLayout />,
    children: [
      {
        path: "",
        element: <Admin />,
        children: [{ path: "organization/:orgId", element: <Organization /> }],
      },
      {
        path: "",
        element: <Admin />,
        children: [
          { path: "users/all", element: <AllUsers /> },
          { path: "users/unassigned", element: <UnassignedUsers /> },
          { path: "users/pending", element: <PendingUsers /> },
        ],
      },
    ],
  },
]);

const redirectToNobocap = () => {
  window.location.href = "https://portal.nobocap.eu/login";
}

export default function App() {
  return <RouterProvider router={router} />;
}
